/**
 * eventRateThresholds — per-event expected ceiling for emits/sec.
 *
 * The EventRateOverlay highlights any event whose rolling 1-second count
 * exceeds its threshold. Used to flag double-fires + runaway loops.
 *
 * Thresholds are deliberately loose — they're "obviously wrong" gates, not
 * tight performance gates. An event that should fire on user input might
 * spike to 5/sec briefly during fast click; one that should fire ≤1/sec
 * shouldn't be hitting 30/sec.
 *
 * Where the server enforces a HARD limit (e.g. playerInput's 120/sec
 * SlidingWindowRateLimiter in MovementHandler), the threshold is set
 * BELOW the cap so the overlay surfaces "approaching the limit" before
 * server-side dropping kicks in. The cap is referenced in the comment.
 *
 * Anything not in this map uses DEFAULT_THRESHOLD.
 *
 * Phase G.4 audit (2026-05-12) — verified all server-side rate limits
 * still in place per the 2026-05-06 lag-reduction arc. Server broadcasts
 * are intentionally NOT throttled (combat correctness requires immediate
 * fire); per-event burst caps not at the wrapper level. The thresholds
 * below approximate "obviously-wrong" rates for those broadcasts.
 */

export const DEFAULT_THRESHOLD = 60;

export const EVENT_RATE_THRESHOLDS: Record<string, number> = {
  // ── server-state / network sync ─────────────────────────────────────
  // Server enforces 120/sec hard cap (MovementHandler.PLAYER_INPUT_LIMIT).
  // Threshold set lower so the overlay flags before the cap drops messages.
  'playerInput': 90,
  'net:message:simLive': 5,
  'net:message:playerDamaged': 15,
  'net:message:npcDamaged': 30,
  'net:message:mobRespawning': 5,
  'net:message:mobRespawned': 5,
  'net:message:eventRate': 5, // Phase B server broadcaster — ~2/sec by design
  'state:update': 60,

  // ── combat ──────────────────────────────────────────────────────────
  'combat:melee_hit': 10,
  'combat:area_hit': 10,
  'combat:npcDamaged': 30,
  'combat:playerAttack': 10,
  'combat:targetChanged': 5,
  'combat:death': 5,
  'COMBAT_NPC_DAMAGED': 30,
  'COMBAT_PLAYER_DAMAGED': 15,

  // ── UI / lifecycle ──────────────────────────────────────────────────
  'GAME_SCENE_READY': 1,
  'LEVEL_LOADED': 1,
  'CAMERA_SETTINGS_CHANGED': 5,
  'player:addXPIntent': 5,
  'PLAYER_ACTION_INTENT': 30,
};

export function thresholdFor(eventName: string): number {
  return EVENT_RATE_THRESHOLDS[eventName] ?? DEFAULT_THRESHOLD;
}
